import React from 'react';
import { TrendingUp, TrendingDown, Minus, Share2 } from 'lucide-react';
import { formatPct, growthPct, shortDate } from '../../lib/format';
import { convertedRows } from '../../lib/currency';
import { useSort, SortableTh } from '../SortableTable';
import { MoneyCell, PnLMoney } from './MoneyMode';

const GrowthCell = ({ curr, prev }) => {
  const g = growthPct(curr, prev);
  if (g === null || g === 0) {
    return <span className="chip chip-neutral"><Minus size={10} />0.00%</span>;
  }
  const positive = g > 0;
  return (
    <span className={`chip ${positive ? 'chip-positive' : 'chip-negative'}`}>
      {positive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
      {formatPct(g)}
    </span>
  );
};

const PeriodSummaryTable = ({ snapshots, holdings, currency, fxRates }) => {
  const rows = snapshots.map((s) => {
    const enriched = convertedRows(holdings[s.id], fxRates, s.id);
    const cost = enriched.reduce((a, x) => a + x.cost, 0);
    const mv = enriched.reduce((a, x) => a + x.mv, 0);
    return { snap: s, cost, marketValue: mv, pnl: mv - cost, pnlPct: cost > 0 ? ((mv - cost) / cost) * 100 : 0 };
  });

  const { sort, onSort, sortRows } = useSort({ key: null, dir: 'asc' });
  const sorted = sortRows(rows, {
    period: (r) => r.snap.date,
    cost: (r) => r.cost,
    marketValue: (r) => r.marketValue,
    pnl: (r) => r.pnl,
    pnlPct: (r) => r.pnlPct,
  });

  return (
    <table className="grid-table" data-testid="period-summary-table">
      <thead>
        <tr>
          <SortableTh sortKey="period" sort={sort} onSort={onSort}>Period</SortableTh>
          <SortableTh sortKey="cost" sort={sort} onSort={onSort} numeric>Total Cost</SortableTh>
          <SortableTh sortKey="marketValue" sort={sort} onSort={onSort} numeric>Market Value</SortableTh>
          <SortableTh sortKey="pnl" sort={sort} onSort={onSort} numeric>Unrealised P&amp;L</SortableTh>
          <SortableTh sortKey="pnlPct" sort={sort} onSort={onSort} numeric>P&amp;L %</SortableTh>
          <th className="num">vs Previous</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => {
          const idxInOriginal = rows.findIndex((x) => x.snap.id === r.snap.id);
          const prev = idxInOriginal > 0 ? rows[idxInOriginal - 1] : null;
          return (
            <tr key={r.snap.id}>
              <td className="font-heading font-semibold">{r.snap.label}
                <span className="text-[11px] text-[#787872] font-mono ml-2">{shortDate(r.snap.date)}</span>
                {r.snap.shared_from && (
                  <span className="chip chip-warning text-[8px] ml-2 align-middle" title={`Shared by ${r.snap.shared_from.email} (${r.snap.shared_role})`}>
                    <Share2 size={8} /> shared
                  </span>
                )}
              </td>
              <td className="num"><MoneyCell value={r.cost} currency={currency} /></td>
              <td className="num"><MoneyCell value={r.marketValue} currency={currency} /></td>
              <td className="num"><PnLMoney value={r.pnl} currency={currency} /></td>
              <td className={`num ${r.pnlPct >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>
                {formatPct(r.pnlPct)}
              </td>
              <td className="num">
                {prev ? <GrowthCell curr={r.marketValue} prev={prev.marketValue} /> : <span className="text-[#AAA]">—</span>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default PeriodSummaryTable;
